<!-- src/components/common/Image.svelte -->
<script>
  import { onMount } from 'svelte';
  
  /**
   * Responsive image component with standardized sizes and progressive enhancement
   * @param {string} src - The path to the image (with or without extension)
   * @param {string} alt - Alt text for the image
   * @param {string} extension - The image extension (e.g., 'jpg', 'png')
   * @param {'lazy'|'eager'} loading - Loading strategy ('lazy' or 'eager')
   * @param {number} width - The width of the image
   * @param {number} height - The height of the image
   * @param {string} class_name - Additional CSS classes to apply
   * @param {boolean} is_lcp - Whether this image is the Largest Contentful Paint element
   * @param {string} sizes_override - Custom sizes attribute to override default calculation
   * @param {boolean} progressive - Whether to enable progressive enhancement (default: true)
   */
  export let src;
  export let alt;
  export let extension = 'webp';
  /** @type {'lazy'|'eager'} */
  export let loading = 'lazy';
  export let width;
  export let height;
  export let class_name = '';
  export let is_lcp = false;
  export let sizes_override = '';
  export let progressive = true;

  // Remove extension if present in src, but keep the directory structure
  const base_path = src.replace(/\.[^/.]+$/, '');
  
  // Define sizes and their widths
  const sizes = [
    { width: 150, suffix: 'xs' },
    { width: 400, suffix: 'sm' },
    { width: 800, suffix: 'md' },
    { width: 1200, suffix: 'lg' },
    { width: 1600, suffix: 'xl' }
  ];

  // Choose best size based on display dimensions
  // If image is displayed very small (like in cards at 150px),
  // we should use the smallest variant to save bandwidth
  const getOptimalImageSize = (displayWidth) => {
    if (displayWidth <= 150) return 'xs';
    if (displayWidth <= 400) return 'sm';
    if (displayWidth <= 800) return 'md';
    if (displayWidth <= 1200) return 'lg';
    return 'xl';
  };

  // Generate srcset
  const srcset = sizes
    .map(size => `${base_path}-${size.suffix}.${extension} ${size.width}w`)
    .join(', ');

  // Generate sizes attribute - more accurately reflect actual display sizes
  // The default sizes attribute was causing browsers to select the largest image too often
  const default_sizes_attr = `
    (max-width: 480px) 300px,
    (max-width: 768px) 400px,
    (max-width: 1024px) 600px,
    (max-width: 1280px) 800px,
    900px
  `.trim();
  
  // Use override if provided, otherwise use improved default
  const sizes_attr = sizes_override || default_sizes_attr;

  // Select optimal image size based on actual display width
  // For card thumbnails (width <= 150px), always use the xs variant (150px)
  // For larger images, select based on device size
  const actual_display_width = width || 800;
  const is_mobile = typeof window !== 'undefined' && window.innerWidth < 768;
  
  // Determine the optimal suffix for the image based on display width
  let default_suffix;
  if (actual_display_width <= 150) {
    default_suffix = 'xs';
  } else if (is_mobile || actual_display_width <= 400) {
    default_suffix = 'sm';
  } else if (actual_display_width <= 800) {
    default_suffix = 'md';
  } else {
    default_suffix = 'lg';
  }
  
  // Use appropriate default size based on estimated viewport
  // In development, use the original source
  const default_src = import.meta.env.DEV ? src : `${base_path}-${default_suffix}.${extension}`;

  // Combine classes
  const combined_class = `responsive-image ${class_name}`.trim();

  // For progressive enhancement
  let img_element;
  let has_loaded_low_res = false;
  
  // Determine if we should use progressive enhancement
  // Disable for LCP images to ensure best LCP times
  const should_use_progressive = progressive && !is_lcp;
  
  onMount(() => {
    if (!should_use_progressive || import.meta.env.DEV) return;
    
    // Create an intersection observer to detect when the image is in viewport
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && has_loaded_low_res) {
          // Pre-load higher quality version in the background
          const high_res_suffix = getOptimalImageSize(Math.max(actual_display_width, img_element.clientWidth));
          
          // Don't attempt to load higher quality if we're already at the highest useful quality
          if (high_res_suffix === default_suffix) return;
          
          const high_res_src = `${base_path}-${high_res_suffix}.${extension}`;
          
          const preload_img = new Image();
          preload_img.src = high_res_src;
          
          preload_img.onload = () => {
            // Smoothly transition to the higher quality image
            img_element.src = high_res_src;
            observer.disconnect();
          };
        }
      });
    }, { threshold: 0.1 });
    
    if (img_element) {
      // When the initial low-res image loads, mark it as loaded
      img_element.onload = () => {
        has_loaded_low_res = true;
      };
      
      observer.observe(img_element);
    }
    
    return () => {
      if (observer) observer.disconnect();
    };
  });

  // If this is the LCP image and we're not in dev mode, we want to inform the parent
  if (is_lcp && !import.meta.env.DEV) {
    // Dispatch an event up to inform parent components that this is the LCP
    onMount(() => {
      document.dispatchEvent(new CustomEvent('lcp-image-detected', { 
        detail: { 
          src: default_src,
          srcset 
        }
      }));
    });
  }
</script>

<img
  bind:this={img_element}
  src={default_src}
  srcset={import.meta.env.DEV || !should_use_progressive ? srcset : undefined}
  sizes={import.meta.env.DEV || !should_use_progressive ? sizes_attr : undefined}
  alt={alt}
  loading={loading}
  width={width}
  height={height}
  class={combined_class}
  fetchpriority={is_lcp ? "high" : "auto"}
  style="object-fit: cover;"
/>

<style>
  /* Base responsive image styles are now global */
  :global(.responsive-image) {
    max-width: 100%;
    display: block;
    transition: filter 0.2s ease-out;
  }
  
  /* Add slight blur when images are loading for a smoother transition */
  :global(.responsive-image[loading="lazy"]:not([src])) {
    filter: blur(5px);
  }
  
  :global(.responsive-image.loaded) {
    filter: blur(0);
  }
</style> 